import { QRCodeSVG } from "qrcode.react";
import { type FC } from "react";

import {
  type ClipboardStatus,
  useClipboard,
} from "../../../hooks/useClipboard";
import { type TeamsBindLink, formatBindLinkMintedLine } from "./helpers";

// ---------------------------------------------------------------------------
// TeamsBindLinkPanel — QR + copy + open-in-Teams for a minted binding token
// ---------------------------------------------------------------------------

/** Lifted verbatim from the former InfoTab god component. */
export const TeamsBindLinkPanel: FC<{
  copyStatusOverride?: ClipboardStatus;
  link: TeamsBindLink;
}> = ({ copyStatusOverride, link }) => {
  const { copy, status } = useClipboard({ resetDelay: 1500 });
  const effectiveStatus = copyStatusOverride ?? status;
  const copied = effectiveStatus === "copied";
  return (
    <div className="max-w-full border border-cyan-400/25 bg-slate-50/80 p-2 shadow-[0_0_18px_rgba(34,211,238,0.08)] dark:bg-slate-950/80">
      <div className="mb-2 font-mono text-[9px] font-bold uppercase tracking-[0.24em] text-cyan-700/90 dark:text-cyan-300/80">
        [ TRANSMIT ]
      </div>
      <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_72px]">
        <div className="min-w-0 space-y-2">
          <div className="flex min-w-0 border border-slate-300 bg-slate-100/80 dark:border-slate-700 dark:bg-slate-900/80">
            <input
              aria-label="Teams binding link"
              className="min-w-0 flex-1 bg-transparent px-2 py-1.5 font-mono text-[10px] text-cyan-900 outline-none dark:text-cyan-100"
              readOnly
              value={link.url}
            />
            <button
              className={`border-l px-2 font-mono text-[10px] font-bold uppercase tracking-[0.16em] transition-colors ${
                copied
                  ? "border-emerald-500/60 text-emerald-700 dark:border-emerald-400/50 dark:text-emerald-300"
                  : "border-slate-300 text-cyan-700 hover:bg-cyan-400/10 dark:border-slate-700 dark:text-cyan-300"
              }`}
              onClick={() => void copy(link.url)}
              type="button"
            >
              {copyLabel(effectiveStatus)}
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            <a
              className="border border-cyan-500/50 px-2 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.16em] text-cyan-800 transition-colors hover:border-cyan-400 hover:bg-cyan-400/10 dark:border-cyan-400/40 dark:text-cyan-200 dark:hover:border-cyan-300"
              href={link.url}
              rel="noreferrer"
              target="_blank"
            >
              Open in Microsoft Teams
            </a>
            <button
              className="border border-slate-300 px-2 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.16em] text-slate-700 transition-colors hover:border-cyan-400/50 hover:text-cyan-700 dark:border-slate-700 dark:text-slate-300 dark:hover:text-cyan-200"
              onClick={() => void copy(link.url)}
              type="button"
            >
              Copy link
            </button>
          </div>
          <p className="font-mono text-[10px] text-emerald-700/90 dark:text-emerald-300/80">
            {formatBindLinkMintedLine(link)}
          </p>
          <p className="font-mono text-[10px] text-slate-400">
            Send the link into a Teams chat — the bot will render a Bind ARTI
            here? card.
          </p>
        </div>
        <div className="flex h-[72px] w-[72px] items-center justify-center border border-cyan-400/25 bg-white p-1">
          <QRCodeSVG
            value={link.url}
            size={62}
            bgColor="#ffffff"
            fgColor="#020617"
            level="M"
          />
        </div>
      </div>
    </div>
  );
};

function copyLabel(status: ClipboardStatus): string {
  switch (status) {
    case "copied": {
      return "✓";
    }
    case "failed": {
      return "Retry";
    }
    case "idle": {
      return "Copy";
    }
  }
}
